import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
  ActivityIndicator,
} from "react-native";
import React, { useState, useEffect } from "react";
import { db, auth } from "../firebase";
import { collection, getDocs, doc, getDoc, setDoc, query, where } from "firebase/firestore";
import { useStateContext } from "../lib/context";
import { Ionicons } from "@expo/vector-icons";

const SeleccionarCliente = ({ navigation, route }) => {
  const { setCurrentUser, setIsViajante, setCorreoViajante } =
    useStateContext();
  const [search, setSearch] = useState("");
  const [clientes, setClientes] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [recentClients, setRecentClients] = useState([]);
  const [pendingCarts, setPendingCarts] = useState({});
  const [loading, setLoading] = useState(true);

  setIsViajante(true);
  setCorreoViajante(route.params);

  // Fetch clients filtered by the Viajante's Vendedor IDs + recent clients
  useEffect(() => {
    const fetchData = async () => {
      try {
        const viajanteEmail = route.params;
        if (!viajanteEmail) return;

        // 1. Get Viajante doc
        const viajanteRef = doc(db, "viajantes", viajanteEmail);
        const viajanteSnap = await getDoc(viajanteRef);

        if (viajanteSnap.exists()) {
          const viajanteData = viajanteSnap.data();

          // Get recent clients from viajante data
          if (viajanteData.recentClients && Array.isArray(viajanteData.recentClients)) {
            setRecentClients(viajanteData.recentClients);

            // Check pending carts for each recent client
            const pendingResults = await Promise.all(
              viajanteData.recentClients.map(async (c) => {
                try {
                  const userSnap = await getDoc(doc(db, "users", c.CUIT + "@mail.com"));
                  const cart = userSnap.exists() ? userSnap.data().currentCart : null;
                  return { cuit: c.CUIT, pending: Array.isArray(cart) && cart.length > 0 };
                } catch {
                  return { cuit: c.CUIT, pending: false };
                }
              })
            );
            const pendingMap = {};
            pendingResults.forEach(({ cuit, pending }) => { pendingMap[cuit] = pending; });
            setPendingCarts(pendingMap);
          }

          // Support both old structure (vendedor3C) and new structure (vendedores3C)
          let vendedorIDs = [];

          if (viajanteData.vendedores3C && Array.isArray(viajanteData.vendedores3C)) {
            // New structure: array of vendedores
            vendedorIDs = viajanteData.vendedores3C
              .map((v) => v.ID || v.id)
              .filter(Boolean);
          } else if (viajanteData.vendedor3C) {
            // Old structure: single vendedor (backwards compatibility)
            const singleID = viajanteData.vendedor3C.ID || viajanteData.vendedor3C.id;
            if (singleID) vendedorIDs = [singleID];
          }

          if (vendedorIDs.length > 0) {
            // 2. Query clients where VENDEDOR__ID matches ANY of the IDs
            // Firestore 'in' operator supports up to 30 values
            const q = query(
              collection(db, "clientesdrn"),
              where("VENDEDOR__ID", "in", vendedorIDs)
            );

            const querySnapshot = await getDocs(q);
            const clientsList = [];
            querySnapshot.forEach((doc) => {
              clientsList.push(doc.data());
            });
            setClientes(clientsList);
          } else {
            console.log("No Vendedor IDs found for this Viajante");
            setClientes([]);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [route.params]);

  // Filter as user types
  useEffect(() => {
    if (search.length > 2) {
      setFiltered(
        clientes.filter(
          (c) => c.CUIT && c.CUIT.toLowerCase().includes(search.toLowerCase())
        )
      );
    } else {
      setFiltered([]);
    }
  }, [search, clientes]);

  const handleClientSelection = async (cliente) => {
    try {
      // Save to recent clients
      const viajanteEmail = route.params;
      const newRecentClient = {
        CUIT: cliente.CUIT,
        APELLIDO: cliente.APELLIDO || "",
        selectedAt: new Date().toISOString(),
      };

      // Update recent clients (keep last 5)
      const updatedRecent = [
        newRecentClient,
        ...recentClients.filter(c => c.CUIT !== cliente.CUIT)
      ].slice(0, 5);

      // Save to Firestore
      await setDoc(
        doc(db, "viajantes", viajanteEmail),
        { recentClients: updatedRecent },
        { merge: true }
      );

      // Navigate
      const userEmail = cliente.CUIT + "@mail.com";
      setCurrentUser(userEmail);
      console.log("Here's the info I've!! before navigate", cliente);
      navigation.navigate("Home", userEmail);
    } catch (error) {
      console.error("Error saving recent client:", error);
      // Still navigate even if save fails
      const userEmail = cliente.CUIT + "@mail.com";
      setCurrentUser(userEmail);
      navigation.navigate("Home", userEmail);
    }
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigation.navigate("LoginViajantes");
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  if (loading) {
    return (
      <View style={localStyles.container}>
        <ActivityIndicator size="large" color="#2a2a8e" />
      </View>
    );
  }

  return (
    <View style={localStyles.container}>
      {/* Header Section */}
      <View style={localStyles.header}>
        <Text style={localStyles.title}>Seleccionar Cliente</Text>
        <TouchableOpacity
          onPress={() => navigation.navigate("OrdersViajantes")}
          style={localStyles.ordersButton}
        >
          <Ionicons name="receipt-outline" size={20} color="#fff" />
          <Text style={localStyles.ordersButtonText}>Mis Pedidos</Text>
        </TouchableOpacity>
      </View>

      <ScrollView 
        style={localStyles.scrollView}
        contentContainerStyle={localStyles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* Recent Clients Section */}
        {recentClients.length > 0 && (
          <View style={localStyles.section}>
            <View style={localStyles.sectionHeader}>
              <Ionicons name="time-outline" size={20} color="#2a2a8e" />
              <Text style={localStyles.sectionTitle}>Recientes</Text>
            </View>
            {recentClients.map((recentCliente, index) => {
              // Find the full client object from clientes array
              const fullCliente = clientes.find(c => c.CUIT === recentCliente.CUIT) || {
                CUIT: recentCliente.CUIT,
                APELLIDO: recentCliente.APELLIDO
              };
              
              const isPending = pendingCarts[recentCliente.CUIT];
              return (
                <TouchableOpacity
                  key={`recent-${recentCliente.CUIT}-${index}`}
                  style={localStyles.recentClientCard}
                  onPress={() =>{
                    console.log("this is the recent client", fullCliente);
                    handleClientSelection(fullCliente)}}
                >
                  <View style={localStyles.clientInfo}>
                    <View style={localStyles.clientNameRow}>
                      <Text style={localStyles.clientCUIT}>{recentCliente.CUIT}</Text>
                      {isPending && (
                        <View style={localStyles.pendingBadge}>
                          <Text style={localStyles.pendingBadgeText}>Pendiente</Text>
                        </View>
                      )}
                    </View>
                    <Text style={localStyles.clientName}>{recentCliente.APELLIDO}</Text>
                  </View>
                  <Ionicons name="chevron-forward" size={20} color="#999" />
                </TouchableOpacity>
              );
            })}
          </View>
        )}

        {/* Search Section */}
        <View style={localStyles.section}>
          <View style={localStyles.sectionHeader}>
            <Ionicons name="search-outline" size={20} color="#2a2a8e" />
            <Text style={localStyles.sectionTitle}>Buscar Cliente</Text>
          </View>
          <View style={localStyles.searchContainer}>
            <Ionicons name="search" size={20} color="#999" style={localStyles.searchIcon} />
            <TextInput
              placeholder="Ingrese CUIT del cliente"
              value={search}
              onChangeText={setSearch}
              style={localStyles.searchInput}
              placeholderTextColor="#999"
            />
          </View>

          {/* Search Results */}
          {search.length > 2 && (
            <View style={localStyles.resultsContainer}>
              {filtered.length > 0 ? (
                filtered.map((cliente) => (
                  <TouchableOpacity
                    key={cliente.CUIT}
                    style={localStyles.clientCard}
                    onPress={()=>{
                      console.log("this is the cliente", cliente);
                      handleClientSelection(cliente)}}
                  >
                    <View style={localStyles.clientInfo}>
                      <Text style={localStyles.clientCUIT}>{cliente.CUIT}</Text>
                      <Text style={localStyles.clientName}>{cliente.APELLIDO || "Sin nombre"}</Text>
                    </View>
                    <Ionicons name="chevron-forward" size={20} color="#2a2a8e" />
                  </TouchableOpacity>
                ))
              ) : (
                <Text style={localStyles.noResults}>No se encontraron clientes</Text>
              )}
            </View>
          )}
        </View>
      </ScrollView>

      {/* Logout Button */}
      <TouchableOpacity
        style={localStyles.logoutButton}
        onPress={handleLogout}
      >
        <Ionicons name="log-out-outline" size={20} color="#fff" />
        <Text style={localStyles.logoutButtonText}>Cerrar Sesión</Text>
      </TouchableOpacity>
    </View>
  );
};

const localStyles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F4F6FD",
  },
  header: {
    backgroundColor: "#2a2a8e",
    paddingTop: 50,
    paddingBottom: 20,
    paddingHorizontal: 20,
    borderBottomLeftRadius: 20,
    borderBottomRightRadius: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  title: {
    fontSize: 26,
    fontWeight: "bold",
    color: "#fff",
    marginBottom: 12,
  },
  ordersButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "rgba(255,255,255,0.2)",
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignSelf: "flex-start",
  },
  ordersButtonText: {
    color: "#fff",
    fontWeight: "600",
    marginLeft: 8,
    fontSize: 15,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 20,
    paddingBottom: 100,
  },
  section: {
    marginBottom: 24,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "#2a2a8e",
    marginLeft: 8,
  },
  recentClientCard: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#fff",
    padding: 16,
    borderRadius: 12,
    marginBottom: 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#fff",
    borderRadius: 12,
    paddingHorizontal: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  searchIcon: {
    marginRight: 10,
  },
  searchInput: {
    flex: 1,
    paddingVertical: 14,
    fontSize: 16,
    color: "#333",
  },
  resultsContainer: {
    marginTop: 12,
  },
  clientCard: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#fff",
    padding: 16,
    borderRadius: 12,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: "#2a2a8e",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  clientInfo: {
    flex: 1,
  },
  clientNameRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  pendingBadge: {
    backgroundColor: "#FFF3E0",
    borderWidth: 1,
    borderColor: "#FF9800",
    borderRadius: 6,
    paddingHorizontal: 7,
    paddingVertical: 2,
  },
  pendingBadgeText: {
    fontSize: 11,
    fontWeight: "600",
    color: "#E65100",
  },
  clientCUIT: {
    fontSize: 16,
    fontWeight: "600",
    color: "#2a2a8e",
    marginBottom: 4,
  },
  clientName: {
    fontSize: 14,
    color: "#666",
  },
  noResults: {
    textAlign: "center",
    color: "#999",
    fontSize: 15,
    paddingVertical: 20,
  },
  logoutButton: {
    position: "absolute",
    bottom: 20,
    left: 20,
    right: 20,
    backgroundColor: "#ff4444",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 16,
    borderRadius: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 4,
  },
  logoutButtonText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "600",
    marginLeft: 8,
  },
});

export default SeleccionarCliente;
